import { observable, action, computed, toJS } from "mobx";
import { symmetricDifference } from "../utils/helpers";
import runtimeVars from "../configs/runTimeVars";

const noop = () => {};

export default class MissionsSearchStore {
  @observable sortBy;

  @observable limit;

  @observable page;

  @observable search;

  @observable id;

  @observable categories;

  @observable categoryOptions;

  @observable categoryInitOptions;

  @observable skills;

  @observable skillOptions;

  @observable skillInitOptions;

  @observable businessSectors;

  @observable businessSectorOptions;

  @observable businessSectorInitOptions;

  @observable specialties;

  @observable specialtyOptions;

  @observable specialtyInitOptions;

  @observable locations;

  @observable radius;

  @observable startDate;

  @observable startDateRange;

  @observable workType;

  @observable employmentType;

  @observable onSite;

  @observable resultsCount;

  @observable isAlertModalOpen;

  @observable isSideFilterOpen;

  constructor(initialState = {}) {
    this.search = initialState.search || "";
    this.sortBy = initialState.sortBy || "id_DESC";
    this.limit = initialState.limit || 12;
    this.page = initialState.page || 1;
    this.id = initialState.id || "";
    this.categories = initialState.categories || [];
    this.categoryOptions = initialState.categoryOptions || [];
    this.categoryInitOptions = initialState.categoryInitOptions || [];
    this.skills = initialState.skills || [];
    this.skillInitOptions = initialState.skillInitOptions || [];
    this.businessSectors = initialState.businessSectors || [];
    this.businessSectorOptions = initialState.businessSectorOptions || [];
    this.businessSectorInitOptions =
      initialState.businessSectorInitOptions || [];
    this.specialties = initialState.specialties || [];
    this.specialtyOptions = initialState.specialtyOptions || [];
    this.specialtyInitOptions = initialState.specialtyInitOptions || [];
    this.locations = initialState.locations || [];
    this.radius = initialState.radius || 0;
    this.startDate = initialState.startDate || "";
    this.startDateRange = initialState.startDateRange || 0;
    this.workType = initialState.workType || "";
    this.employmentType = initialState.employmentType || "";
    this.onSite = initialState.onSite || "";
    this.resultsCount = initialState.resultsCount || 0;
    this.isAlertModalOpen = initialState.isAlertModalOpen || false;
    this.isSideFilterOpen = initialState.isSideFilterOpen || false;

    if (typeof window !== "undefined") {
      const url = window.location.href;
      const urlSections = url.split("?");
      if (urlSections[1]) {
        let urlSectionCleaned = urlSections[1].replaceAll("+", " ");
        urlSectionCleaned = urlSectionCleaned.replaceAll("%2C", ",");
        urlSectionCleaned = urlSectionCleaned.replaceAll("%2F", "/");
        const paramsList = urlSectionCleaned.split("&");
        for (let i = 0; i < paramsList.length; i++) {
          const cleanedParam = paramsList[i].replaceAll("%26", "&");
          const paramsSections = cleanedParam.split("=");
          if (paramsSections[0] === "search") {
            this.search = paramsSections[1];
          } else if (paramsSections[0] === "employmentType") {
            this.employmentType = paramsSections[1];
          } else if (paramsSections[0] === "skills") {
            this.skills = paramsSections[1].split(",");
          } else if (paramsSections[0] === "specialties") {
            this.specialties = paramsSections[1].split(",");
          } else if (paramsSections[0] === "categories") {
            this.categories = paramsSections[1].split(",");
          } else if (paramsSections[0] === "locations") {
            const locationsList = paramsSections[1].split(",");
            for (let j = 0; j < locationsList.length; j++) {
              let address = {};
              const formattedCity = locationsList[j].slice(" ");
              if (!Number.isNaN(formattedCity[0])) {
                address.city = locationsList[j].substr(
                  locationsList[j].indexOf(" ") + 1
                );
              }
              const arr = this.locations.slice();
              const cityArr = arr.map(item => item.city);
              if (!cityArr.includes(locationsList[j])) {
                arr.push(address);
                this.locations = arr;
              }
            }
          }
        }
      }
    }
  }

  @computed
  get skip() {
    return (this.page - 1) * this.limit;
  }

  @computed
  get printResultsCount() {
    const categoriesJSArray = this.categories.toJS();
    const skillsJSArray = this.skills.toJS();
    const businessSectorsJSArray = this.businessSectors.toJS();
    const specialtiesSectorsJSArray = this.specialties.toJS();
    return (
      this.id ||
      this.search ||
      this.startDate ||
      categoriesJSArray.length > 0 ||
      skillsJSArray.length > 0 ||
      businessSectorsJSArray.length > 0 ||
      specialtiesSectorsJSArray.length > 0 ||
      this.cityLabels.length > 0
    );
  }

  @computed
  get canOpenAlertModal() {
    const categoriesJSArray = this.categories.toJS();
    const skillsJSArray = this.skills.toJS();
    const businessSectorsJSArray = this.businessSectors.toJS();
    const specialtiesSectorsJSArray = this.specialties.toJS();
    return (
      categoriesJSArray.length ||
      skillsJSArray.length ||
      businessSectorsJSArray.length ||
      specialtiesSectorsJSArray.length ||
      this.cityLabels.length ||
      this.workType ||
      this.employmentType ||
      this.onSite
    );
  }

  @computed
  get cityLabels() {
    const locationsJSArray = this.locations.toJS();
    return locationsJSArray.map(item => item.city);
  }

  @action
  createSearchUrl = () => {
    let params = {};
    if (this.search) {
      params.search = this.search;
    }
    if (this.skills && this.skills.length > 0) {
      let skillsList = this.skills[0];
      for (let i = 1; i < this.skills.length; i++)
        skillsList = skillsList.concat(",", this.skills[i]);
      params.skills = skillsList;
    }

    if (this.categories && this.categories.length > 0) {
      let categoriesList = this.categories[0];
      for (let i = 1; i < this.categories.length; i++)
        categoriesList = categoriesList.concat(",", this.categories[i]);
      params.categories = categoriesList;
    }

    if (this.specialties && this.specialties.length > 0) {
      let specialtiesList = this.specialties[0];
      for (let i = 1; i < this.specialties.length; i++)
        specialtiesList = specialtiesList.concat(",", this.specialties[i]);
      params.specialties = specialtiesList;
    }

    if (this.locations && this.locations.length > 0) {
      let locationsList = this.locations[0].city;
      for (let i = 1; i < this.locations.length; i++)
        locationsList = locationsList.concat(",", this.locations[i].city);
      params.locations = locationsList;
    }

    if (this.employmentType) {
      params.employmentType = this.employmentType;
    }

    window.history.pushState(
      { type: "missionsSearch" },
      "missions search",
      "?" + new URLSearchParams(params).toString()
    );
  };

  @action
  changeSortBy = (sorter, direction) => {
    this.sortBy = sorter
      .toString()
      .concat("_")
      .concat(direction.toString());
  };

  @action
  changePage = current => {
    this.page = current;
  };

  @action
  changeSearch = value => {
    this.search = value;
  };

  @action
  searchSubmit = value => {
    if (value[0] === "#") {
      this.id = value.substring(1);
      this.search = "";
      this.page = 1;
    } else {
      this.id = "";
      this.sortBy = null;
      this.search = value;
      this.page = 1;
    }

    this.createSearchUrl();
  };

  @action
  changeResultsCount = value => {
    this.resultsCount = value;
  };

  @action
  removeCategory = value => {
    // the remove function here is a MobX observable array built in function
    this.categories.remove(value);
    this.page = 1;
    this.createSearchUrl();
  };

  @action
  addCategory = value => {
    // NB: MobX observable arrays are not JS arrays. Therefore the use of slice
    // before applying certain function as push()
    // https://mobx.js.org/best/pitfalls.html
    this.page = 1;
    const arr = this.categories.slice();
    if (!arr.includes(value)) {
      arr.push(value);
      this.categories = arr;
      this.setCategoryOptions();
    }
    this.createSearchUrl();
  };

  @action
  initCategoryOptions = options => {
    this.categoryInitOptions = options;
    this.setCategoryOptions();
  };

  @action
  setCategoryOptions = () => {
    const categoriesJSArray = this.categories.toJS();
    const categoryInitJSArray = this.categoryInitOptions.toJS();

    this.categoryOptions = symmetricDifference(
      categoryInitJSArray,
      categoriesJSArray
    );
  };

  @action
  removeSkill = value => {
    // the remove function here is a MobX observable array built in function
    const arr = this.skills.slice();
    const index = arr.indexOf(value);
    if (index > -1) {
      arr.splice(index, 1);
    }
    this.skills = arr;
    this.page = 1;
    this.setSkillOptions();
    this.createSearchUrl();
  };

  @action
  addSkill = value => {
    // NB: MobX observable arrays are not JS arrays. Therefore the use of slice
    // before applying certain function as push()
    // https://mobx.js.org/best/pitfalls.html
    this.page = 1;
    const arr = this.skills.slice();
    if (!arr.includes(value)) {
      arr.push(value);
      this.skills = arr;
      this.setSkillOptions();
    }
    this.createSearchUrl();
  };

  @action
  initSkillOptions = options => {
    this.skillInitOptions = options;
    this.setSkillOptions();
  };

  @action
  setSkillOptions = () => {
    const skillsJSArray = this.skills.toJS();
    const skillInitJSArray = this.skillInitOptions.toJS();

    this.skillOptions = symmetricDifference(skillInitJSArray, skillsJSArray);
  };

  @action
  removeBusinessSector = value => {
    // the remove function here is a MobX observable array built in function
    this.businessSectors.remove(value);
    this.page = 1;
  };

  @action
  addBusinessSector = value => {
    // NB: MobX observable arrays are not JS arrays. Therefore the use of slice
    // before applying certain function as push()
    // https://mobx.js.org/best/pitfalls.html
    this.page = 1;
    const arr = this.businessSectors.slice();
    if (!arr.includes(value)) {
      arr.push(value);
      this.businessSectors = arr;
      this.setBusinessSectorOptions();
    }
  };

  @action
  initBusinessSectorOptions = options => {
    this.businessSectorInitOptions = options;
    this.setBusinessSectorOptions();
  };

  @action
  setBusinessSectorOptions = () => {
    const businessSectorsJSArray = this.businessSectors.toJS();
    const businessSectorInitJSArray = this.businessSectorInitOptions.toJS();

    this.businessSectorOptions = symmetricDifference(
      businessSectorInitJSArray,
      businessSectorsJSArray
    );
  };

  @action
  removeSpecialty = value => {
    this.specialties.remove(value);
    this.page = 1;
    this.createSearchUrl();
  };

  @action
  addSpecialty = value => {
    this.page = 1;
    const arr = this.specialties.slice();
    if (!arr.includes(value)) {
      arr.push(value);
      this.specialties = arr;
      this.setSpecialtyOptions();
    }
    this.createSearchUrl();
  };

  @action
  initSpecialtyOptions = options => {
    this.specialtyInitOptions = options;
    this.setSpecialtyOptions();
  };

  @action
  setSpecialtyOptions = () => {
    const specialtiesJSArray = this.specialties.toJS();
    const specialtyInitJSArray = this.specialtyInitOptions.toJS();

    this.specialtyOptions = symmetricDifference(
      specialtyInitJSArray,
      specialtiesJSArray
    );
  };

  @action
  addLocation = address => {
    // NB: MobX observable arrays are not JS arrays. Therefore the use of slice
    // before applying certain function as push()
    // https://mobx.js.org/best/pitfalls.html

    const { city } = address;

    const formattedCity = city.slice(" ");
    if (!Number.isNaN(formattedCity[0])) {
      // This line can be optimized by removing the indexOf method
      address.city = city.substr(city.indexOf(" ") + 1);
    }
    this.page = 1;
    const arr = this.locations.slice();
    const cityArr = arr.map(item => item.city);
    if (!cityArr.includes(city)) {
      arr.push(address);
      this.locations = arr;
    }
    this.createSearchUrl();
  };

  @action
  removeLocation = city => {
    const arr = toJS(this.locations);
    const newArr = arr.filter(item => item.city !== city);

    this.page = 1;
    this.locations = newArr;
    this.createSearchUrl();
  };

  @action
  setRadius = val => {
    this.radius = val;
  };

  @action
  changeStartDate = value => {
    this.startDate = value;
  };

  @action
  changeStartDateRange = value => {
    this.page = 1;
    this.startDateRange = value;
  };

  @action
  changeWorkType = value => {
    this.page = 1;
    this.workType = value;
  };

  @action
  changeEmploymentType = value => {
    this.page = 1;
    this.employmentType = value;
    this.createSearchUrl();
  };

  @action
  changeOnSite = value => {
    this.page = 1;
    this.onSite = value;
  };

  @action
  addToFilters = (val, filter) => {
    switch (filter) {
      case "categories":
        this.addCategory(val);
        break;
      case "skills":
        this.addSkill(val);
        break;
      default:
        noop();
    }
  };

  @action
  setAlertModal = () => {
    this.isAlertModalOpen = !this.isAlertModalOpen;
  };

  @action
  setSideFilter = () => {
    this.isSideFilterOpen = !this.isSideFilterOpen;
  };

  @action
  clearFilters = () => {
    this.skills = [];
    this.categories = [];
    this.specialties = [];
    this.locations = [];
    this.radius = 0;
    this.businessSectors = [];
    this.startDate = "";
    this.workType = "";
    this.employmentType = "";
    this.onSite = "";
    this.search = "";
    this.createSearchUrl();
  };
}
